import { useCallback, useEffect, useState } from 'react';
import { Alert, Loading, PageHeader } from '../App';
import { adminApi, date, errorMessage, humanise, money, type ListingPlan } from '../api/admin';

export function PlansPage() {
  const [plans, setPlans] = useState<ListingPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<ListingPlan | 'new' | null>(null);
  const [creatingBranchOffer, setCreatingBranchOffer] = useState(false);
  const [retiringPlanId, setRetiringPlanId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setPlans(await adminApi.plans());
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Grouped by code so every version of a plan is visible together, newest first.
  const grouped = (plans ?? []).reduce<Record<string, ListingPlan[]>>((groups, plan) => {
    (groups[plan.code] ??= []).push(plan);
    return groups;
  }, {});

  const retireBranchPlan = async (plan: ListingPlan) => {
    const confirmed = window.confirm(
      `Hide "${plan.name}" from new owner purchases? Existing subscriptions and payment history will not be changed.`,
    );
    if (!confirmed) return;

    setRetiringPlanId(plan.id);
    setError(null);
    try {
      await adminApi.savePlan({
        code: plan.code,
        name: plan.name,
        description: plan.description,
        category: plan.category,
        billingType: plan.billingType,
        amount: plan.listPrice,
        taxPercentage: plan.taxPercentage,
        durationDays: plan.durationDays,
        promotionalDiscountPercentage: plan.promotionalDiscountPercentage,
        promotionEndsAtUtc: plan.promotionEndsAtUtc ?? null,
        features: plan.features.join('\n') || undefined,
        isDefault: false,
        isActive: false,
        displayOrder: plan.displayOrder,
      });
      setSuccess(`"${plan.name}" is no longer available for new purchases. Existing subscriptions remain unchanged.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRetiringPlanId(null);
    }
  };

  const deletePlan = async (plan: ListingPlan, versionCount: number) => {
    const confirmed = window.confirm(
      `Permanently delete "${plan.name}" (${plan.code}) and all ${versionCount} version(s)? `
      + 'Completed subscription and payment history will be retained with the plan reference detached. '
      + 'Pending payment orders will be cancelled locally only; verify the provider and manually refund if already captured. '
      + 'Default plans and plans configured as managed payment plans cannot be deleted. '
      + 'This action cannot be undone.',
    );
    if (!confirmed) return;

    setRetiringPlanId(plan.id);
    setError(null);
    try {
      await adminApi.deletePlan(plan.code);
      setSuccess(`"${plan.name}" and its pricing plan versions were permanently deleted.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRetiringPlanId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Plans & pricing"
        description="Prices are configuration, not code. Changing commercial terms creates a new version and leaves existing subscriptions on the terms they were sold."
        actions={
          <div className="d-flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreatingBranchOffer(true)}>
              New branch subscription offer
            </button>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setEditing('new')}>
              New other plan
            </button>
          </div>
        }
      />

      <p className="text-muted small">
        Branch offers are scoped to PG/Hostel or Hotel. Active offers appear in the matching owner checkout;
        the default offer stays selected initially. Hiding an offer does not remove subscriptions already sold.
      </p>
      <Alert message={error} />
      <Alert message={success} variant="success" />

      {!plans ? (
        <Loading rows={5} />
      ) : (
        Object.entries(grouped).map(([code, versions]) => {
          const current = versions.find((v) => v.isActive) ?? versions[0];

          return (
            <div className="admin-card mb-3" key={code}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div>
                  <h2 className="h6 mb-1">
                    {current.name} <code className="small text-muted">{code}</code>
                  </h2>
                  <p className="text-muted small mb-2">
                    {current.category ? humanise(current.category) : 'All categories'} ·{' '}
                    {humanise(current.billingType)} · {current.durationDays} days
                    {current.isDefault && <span className="badge bg-primary ms-2">Default</span>}
                    {!current.isActive && <span className="badge bg-secondary ms-2">Hidden from new purchases</span>}
                  </p>

                  <div className="h5 mb-0">{money(current.totalAmount)}</div>
                  <div className="text-muted small">
                    {money(current.amount)} + {current.taxPercentage}% tax
                    {current.discountAmount > 0 && <> · {money(current.discountAmount)} promotional discount</>}
                    {current.promotionalDiscountPercentage > 0 && (
                      <> · {current.promotionalDiscountPercentage}% offer{current.promotionEndsAtUtc
                        ? ` through ${date(current.promotionEndsAtUtc)}`
                        : ''}</>
                    )}
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {!versions.some((version) => version.isDefault) && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => void deletePlan(current, versions.length)}
                      disabled={retiringPlanId === current.id}
                    >
                      {retiringPlanId === current.id ? 'Deleting…' : 'Delete plan'}
                    </button>
                  )}
                  {current.isActive && current.billingType === 'BranchSubscription' && !current.isDefault && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => void retireBranchPlan(current)}
                      disabled={retiringPlanId === current.id}
                    >
                      Hide from owners
                    </button>
                  )}
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(current)}>
                    Edit
                  </button>
                </div>
              </div>

              {versions.length > 1 && (
                <details className="mt-3">
                  <summary className="small text-muted">
                    {versions.length} versions — older ones stay referenced by the subscriptions sold on them
                  </summary>

                  <div className="admin-table-wrap">
                    <table className="admin-table mt-2">
                      <thead>
                        <tr>
                          <th scope="col">Version</th>
                          <th scope="col">Price</th>
                          <th scope="col">Tax</th>
                          <th scope="col">Days</th>
                          <th scope="col">State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions
                          .slice()
                          .sort((a, b) => b.version - a.version)
                          .map((version) => (
                            <tr key={version.id}>
                              <td>v{version.version}</td>
                              <td>{money(version.listPrice)}</td>
                              <td>{version.taxPercentage}%</td>
                              <td>{version.durationDays}</td>
                              <td>
                                <span className={`badge bg-${version.isActive ? 'success' : 'secondary'}`}>
                                  {version.isActive ? 'Current' : 'Retired'}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          );
        })
      )}

      {editing && (
        <PlanDialog
          plan={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onDone={async (message) => {
            setEditing(null);
            setSuccess(message);
            await load();
          }}
          onError={setError}
        />
      )}
      {creatingBranchOffer && (
        <PlanDialog
          plan={null}
          branchSubscriptionOnly
          onCancel={() => setCreatingBranchOffer(false)}
          onDone={async (message) => {
            setCreatingBranchOffer(false);
            setSuccess(message);
            await load();
          }}
          onError={setError}
        />
      )}
    </>
  );
}

function PlanDialog({
  plan,
  branchSubscriptionOnly = false,
  onCancel,
  onDone,
  onError,
}: {
  plan: ListingPlan | null;
  branchSubscriptionOnly?: boolean;
  onCancel: () => void;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [code, setCode] = useState(plan?.code ?? '');
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [category, setCategory] = useState(plan?.category ?? (branchSubscriptionOnly ? 'PgHostel' : ''));
  const [billingType, setBillingType] = useState(plan?.billingType ?? 'BranchSubscription');
  const [amount, setAmount] = useState(String(plan?.listPrice ?? ''));
  const [taxPercentage, setTax] = useState(String(plan?.taxPercentage ?? 18));
  const [durationDays, setDuration] = useState(String(plan?.durationDays ?? 30));
  const [discount, setDiscount] = useState(String(plan?.promotionalDiscountPercentage ?? 0));
  const [promotionEndsAt, setPromotionEndsAt] = useState(
    plan?.promotionEndsAtUtc ? plan.promotionEndsAtUtc.slice(0, 10) : '',
  );
  const [features, setFeatures] = useState(plan?.features.join('\n') ?? '');
  const [isDefault, setIsDefault] = useState(plan?.isDefault ?? false);
  const [isActive, setIsActive] = useState(plan?.isActive ?? true);
  const [busy, setBusy] = useState(false);

  // Whether this edit changes what anyone has agreed to pay. Only those changes create a
  // new version; copy and ordering are edited in place.
  const termsChanged =
    plan !== null &&
    (Number(amount) !== plan.listPrice ||
      Number(taxPercentage) !== plan.taxPercentage ||
      Number(durationDays) !== plan.durationDays ||
      (Number(discount) || 0) !== plan.promotionalDiscountPercentage ||
      promotionEndsAt !== (plan.promotionEndsAtUtc?.slice(0, 10) ?? '') ||
      billingType !== plan.billingType ||
      (category || undefined) !== plan.category);
  const promotionEndsAtUtc = promotionEndsAt
    ? new Date(`${promotionEndsAt}T23:59:59.999+05:30`).toISOString()
    : null;

  const submit = async () => {
    setBusy(true);

    try {
      await adminApi.savePlan({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description: description.trim() || undefined,
        category: category || undefined,
        billingType,
        amount: Number(amount) || 0,
        taxPercentage: Number(taxPercentage) || 0,
        durationDays: Number(durationDays) || 30,
        promotionalDiscountPercentage: Number(discount) || 0,
        promotionEndsAtUtc,
        features: features.trim() || undefined,
        isDefault,
        isActive,
        displayOrder: 0,
      });

      await onDone(
        termsChanged
          ? 'A new plan version was created. Existing subscriptions keep the price they were sold at.'
          : 'Plan saved.',
      );
    } catch (err) {
      onError(errorMessage(err));
      setBusy(false);
    }
  };

  const net = (Number(amount) || 0) * (1 - (Number(discount) || 0) / 100);
  const taxAmount = net * ((Number(taxPercentage) || 0) / 100);

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="plan-title">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="plan-title">
                    {plan ? `Edit ${plan.name}` : branchSubscriptionOnly ? 'New branch subscription offer' : 'New plan'}
              </h2>
            </div>

            <div className="modal-body">
              {termsChanged && (
                <div className="alert alert-info py-2 px-3 small">
                  You are changing commercial terms, so this saves as <strong>version {plan!.version + 1}</strong>.
                  Subscriptions already sold on version {plan!.version} keep their agreed price until they renew.
                </div>
              )}

              <div className="row">
                <div className="col-12 col-md-4">
                  <label className="form-label" htmlFor="plan-code">
                    Code
                  </label>
                  <input
                    id="plan-code"
                    className="form-control mb-3"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={plan !== null}
                    placeholder="PG_SEASONAL_2027"
                  />
                  {branchSubscriptionOnly && (
                    <div className="form-text mb-3">Use a unique code to add this offer alongside the default plan.</div>
                  )}
                </div>

                <div className="col-12 col-md-8">
                  <label className="form-label" htmlFor="plan-name">
                    Name
                  </label>
                  <input
                    id="plan-name"
                    className="form-control mb-3"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="plan-category">
                    Category
                  </label>
                  <select
                    id="plan-category"
                    className="form-select mb-3"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {!branchSubscriptionOnly && <option value="">All categories (add-on)</option>}
                    <option value="PgHostel">PG &amp; Hostel</option>
                    {!branchSubscriptionOnly && <option value="HomeRental">Home rental</option>}
                    <option value="Hotel">Hotel</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="plan-billing">
                    Billing type
                  </label>
                  <select
                    id="plan-billing"
                    className="form-select mb-3"
                    value={billingType}
                    onChange={(e) => setBillingType(e.target.value)}
                    disabled={branchSubscriptionOnly}
                  >
                    <option value="BranchSubscription">Recurring branch subscription</option>
                    <option value="PerListing">One-time per listing</option>
                    <option value="FeaturedPlacement">Featured placement</option>
                  </select>
                </div>

                <div className="col-4">
                  <label className="form-label" htmlFor="plan-amount">
                    Price (before tax)
                  </label>
                  <input
                    id="plan-amount"
                    className="form-control mb-3"
                    inputMode="numeric"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>

                <div className="col-4">
                  <label className="form-label" htmlFor="plan-tax">
                    Tax %
                  </label>
                  <input
                    id="plan-tax"
                    className="form-control mb-3"
                    inputMode="decimal"
                    value={taxPercentage}
                    onChange={(e) => setTax(e.target.value)}
                  />
                </div>

                <div className="col-4">
                  <label className="form-label" htmlFor="plan-days">
                    Validity (days)
                  </label>
                  <input
                    id="plan-days"
                    className="form-control mb-3"
                    inputMode="numeric"
                    value={durationDays}
                    onChange={(e) => setDuration(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="plan-discount">
                    Promotional discount %
                  </label>
                  <input
                    id="plan-discount"
                    className="form-control mb-3"
                    inputMode="decimal"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                  />
                </div>
                {Number(discount) > 0 && (
                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="plan-promotion-ends">
                      Offer ends (optional)
                    </label>
                    <input
                      id="plan-promotion-ends"
                      className="form-control mb-3"
                      type="date"
                      value={promotionEndsAt}
                      onChange={(e) => setPromotionEndsAt(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="admin-card mb-3">
                <div className="d-flex justify-content-between">
                  <span>Owner pays</span>
                  <strong>{money(net + taxAmount)}</strong>
                </div>
                <div className="d-flex justify-content-between text-muted small">
                  <span>
                    {money(net)} + {taxPercentage}% tax
                  </span>
                  <span>{money(taxAmount)}</span>
                </div>
              </div>

              <label className="form-label" htmlFor="plan-features">
                Features, one per line
              </label>
              <textarea
                id="plan-features"
                className="form-control mb-3"
                rows={4}
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
              />

              <label className="form-label" htmlFor="plan-description">
                Description
              </label>
              <textarea
                id="plan-description"
                className="form-control mb-3"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              {!branchSubscriptionOnly && (
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="plan-default"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="plan-default">
                    Offer this by default for its category
                  </label>
                </div>
              )}

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="plan-active"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="plan-active">
                  Available to buy
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
                {busy ? 'Saving…' : termsChanged ? 'Save as new version' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
